/**
 * Top-level app chrome. A single horizontal nav bar with the brand mark,
 * primary destinations, a theme toggle, and the user menu. No sidebar —
 * the portal is shallow (3 top-level surfaces + per-app detail), so a tab
 * bar reads cleaner and leaves the canvas open for content.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import type { MeResponse } from '../api/types.js';
import { useTheme } from '../theme/ThemeProvider.js';
import { Brand } from './Brand.js';
import {
  IconBot,
  IconChevronDown,
  IconCoffee,
  IconLaptop,
  IconMoon,
  IconShield,
  IconSun,
} from './Icons.js';

interface LayoutProps {
  me: MeResponse;
}

function initialsFor(user: MeResponse['user']): string {
  const source = user.name?.trim() || user.email.split('@')[0] || '?';
  const parts = source.split(/\s+/).filter(Boolean).slice(0, 2);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

function Avatar({ user, size = 28 }: { user: MeResponse['user']; size?: number }) {
  const [broken, setBroken] = useState(false);
  if (user.avatarUrl && !broken) {
    return (
      <img
        src={user.avatarUrl}
        alt={user.name ?? user.email}
        width={size}
        height={size}
        onError={() => setBroken(true)}
        className="rounded-full bg-surface-2 object-cover flex-none ring-1 ring-line"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      aria-hidden
      className="rounded-full bg-brand text-brand-fg flex-none flex items-center justify-center font-semibold"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {initialsFor(user)}
    </span>
  );
}

function ThemeToggle() {
  const { preference, cycle } = useTheme();
  const Icon = preference === 'light' ? IconSun : preference === 'dark' ? IconMoon : IconLaptop;
  const label = preference === 'system' ? 'Theme: follow system' : `Theme: ${preference}`;
  return (
    <button
      type="button"
      onClick={cycle}
      title={label}
      aria-label={label}
      className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-muted hover:text-ink hover:bg-surface-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50"
    >
      <Icon size={16} />
    </button>
  );
}

function UserMenu({ me }: { me: MeResponse }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function signOut() {
    // POST /logout on the auth-worker revokes the KV session and clears
    // the .{base}-scoped kin_session cookie, then we bounce to /login so
    // the user lands on a sign-in page (rather than a stale Portal).
    // The Portal IS the env's base domain (Portal at `dev.mkin.app` →
    // auth at `auth.dev.mkin.app`), so the auth host is just
    // `auth.${window.location.hostname}` — NO segment stripping (that
    // sends a dev sign-out to the production `auth.mkin.app`).
    const authBase = `https://auth.${window.location.hostname}`;
    fetch(`${authBase}/logout`, { method: 'POST', credentials: 'include' })
      .catch(() => {
        // Swallow: even if the revoke call fails, we still bounce the
        // browser away; the cookie write may not have flushed but the
        // user perceives sign-out either way.
      })
      .finally(() => {
        const returnTo = encodeURIComponent(window.location.origin + '/');
        window.location.href = `${authBase}/login?return_to=${returnTo}`;
      });
  }

  const displayName = me.user.name ?? me.user.email;
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full pl-0.5 pr-2 py-0.5 hover:bg-surface-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Avatar user={me.user} />
        <span className="text-sm text-ink max-w-[140px] truncate hidden sm:inline">
          {displayName}
        </span>
        <IconChevronDown size={12} className="text-muted" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-72 rounded-xl border border-line bg-surface shadow-elevated z-30 animate-scale-in origin-top-right"
        >
          <div className="px-4 py-3.5 border-b border-line flex items-center gap-3">
            <Avatar user={me.user} size={40} />
            <div className="min-w-0">
              {me.user.name && (
                <div className="text-sm font-medium truncate text-ink">{me.user.name}</div>
              )}
              <div className="text-xs text-muted truncate">{me.user.email}</div>
              {me.globalRole === 'platform_admin' && (
                <div className="text-[10px] uppercase tracking-wider text-brand font-semibold mt-1">
                  Platform admin
                </div>
              )}
            </div>
          </div>
          {me.globalRole === 'platform_admin' && (
            <Link
              to="/admin"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-surface-2 text-ink transition-colors border-b border-line"
            >
              <IconShield size={14} className="text-muted" />
              <span>Admin</span>
            </Link>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={signOut}
            className="w-full text-left px-4 py-2.5 text-sm hover:bg-surface-2 text-ink transition-colors"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Apps', end: true },
  { to: '/docs', label: 'Docs' },
];

// Admin used to live in the primary nav. It's now a row inside the user
// fly-out (rendered only for platform_admins) so the mobile nav doesn't
// overflow once you add Docs.

function PrimaryNav() {
  return (
    <nav className="flex items-center gap-1" aria-label="Primary">
      {NAV_ITEMS.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          {...(item.end ? { end: true } : {})}
          className={({ isActive }) =>
            'relative px-3 py-1.5 rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 ' +
            (isActive ? 'text-ink bg-surface-2' : 'text-muted hover:text-ink hover:bg-surface-2/60')
          }
        >
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

export function Layout({ me }: LayoutProps) {
  return (
    <div className="min-h-full flex flex-col">
      <header className="sticky top-0 z-20 backdrop-blur-md bg-bg/80 border-b border-line">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6 min-w-0">
            <NavLink
              to="/"
              aria-label="Kin home"
              className="flex items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50"
            >
              <Brand height={42} />
            </NavLink>
            <span aria-hidden className="hidden sm:block h-5 w-px bg-line" />
            <div className="hidden sm:block">
              <PrimaryNav />
            </div>
          </div>
          <div className="flex items-center gap-1">
            <div className="sm:hidden">
              <PrimaryNav />
            </div>
            <ThemeToggle />
            <UserMenu me={me} />
          </div>
        </div>
      </header>
      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 animate-fade-in">
          <Outlet />
        </div>
      </main>
      <footer className="border-t border-line">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between text-xs text-muted">
          <span>Kin · Mytra internal</span>
          <a
            href="https://github.com/mytra"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 hover:text-ink transition-colors group"
          >
            <span>Powered by</span>
            <IconCoffee
              size={13}
              className="text-warn group-hover:scale-110 transition-transform"
            />
            <span>and</span>
            <IconBot size={13} className="text-brand group-hover:scale-110 transition-transform" />
            <span>at Mytra</span>
          </a>
        </div>
      </footer>
    </div>
  );
}
